import { map, difference, isFunction, isEmpty } from 'underscore';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';

const getSelectionStats = (initialOptions, currentOptions) => {
  const initialOptionKeys = map(initialOptions, option => option.key);
  const currentOptionKeys = map(currentOptions, option => option.key);

  const addedOptionKeys = difference(currentOptionKeys, initialOptionKeys);
  const deletedOptionKeys = difference(initialOptionKeys, currentOptionKeys);

  return [deletedOptionKeys, addedOptionKeys];
};

export const makeUseSelectedOptionsAsyncDataSource = (loadSelectedOptions) => {
  const useSelectedOptionsAsyncDataSource = (selectedOptionsLimit, onChange, savedAddedOptionKeys, savedDeletedOptionKeys) => {
    const [isLoading, setIsLoading] = useState(true);
    const asyncTokenRef = useRef();

    const [options, setOptions] = useState([]);
    const initialOptions = useRef([]);
    const [loadError, setLoadError] = useState();

    const onDoneLoading = useCallback((newOptions) => {
      setIsLoading(false);
      asyncTokenRef.current = null;
      setOptions(newOptions);
      initialOptions.current = newOptions;
    }, []);
    const onErrorLoading = useCallback((error) => {
      setIsLoading(false);
      asyncTokenRef.current = null;
      setLoadError(error);
    }, []);
    const onCancelLoading = useCallback(() => {
      setIsLoading(false);
      asyncTokenRef.current = null;
    }, []);
    const handleOptionsChangeByUser = useCallback((newOptions) => {
      if (isFunction(onChange)) {
        const [deletedOptionKeys, addedOptionKeys] = getSelectionStats(initialOptions.current, newOptions);
        onChange(newOptions, [deletedOptionKeys, addedOptionKeys]);
      }
    }, [onChange]);
    const setOptionsByUser = useCallback((newOptions) => {
      setOptions(newOptions);
      handleOptionsChangeByUser(newOptions);
    }, [handleOptionsChangeByUser]);

    useEffect(() => {
      setIsLoading(true);
      if (asyncTokenRef.current) {
        asyncTokenRef.current.cancel();
      }
      asyncTokenRef.current = createAsyncToken(loadSelectedOptions())
        .on({
          onSuccess: onDoneLoading,
          onError: onErrorLoading,
          onCancel: onCancelLoading,
        });
    }, [onDoneLoading, onErrorLoading, onCancelLoading]);

    useEffect(() => {
      // After we loaded and we receive updates from the parent about some changes saved, we need to update the initial options so that future user changes are compared against the new base line of selected options
      // We also notify the parent/subscriber via onChange about the new diff
      if (!isLoading && (!isEmpty(savedAddedOptionKeys) || !isEmpty(savedDeletedOptionKeys))) {
        const newInitialOptions = [
          ...initialOptions.current.filter(option => !savedDeletedOptionKeys.includes(option.key)),
          ...options.filter(option => savedAddedOptionKeys.includes(option.key)),
        ];

        initialOptions.current = newInitialOptions;
        if (isFunction(onChange)) {
          const [deletedOptionKeys, addedOptionKeys] = getSelectionStats(newInitialOptions, options);
          onChange(options, [deletedOptionKeys, addedOptionKeys]);
        }
      }
    }, [isLoading, options, savedAddedOptionKeys, savedDeletedOptionKeys, onChange]);

    const isSelectedOptionsLimitReached = useMemo(() => options.length >= selectedOptionsLimit, [options, selectedOptionsLimit]);

    return [isLoading, options, initialOptions.current, loadError, setOptionsByUser, isSelectedOptionsLimitReached];
  };

  return useSelectedOptionsAsyncDataSource;
};
